// Comprehensive Technical Indicator Calculations for Live Chart and Replay

export function calculateIchimoku(bars, params = {}) {
  const tenkanPeriod = params.tenkan || 9;
  const kijunPeriod = params.kijun || 26;
  const senkouBPeriod = params.senkouB || 52;
  const displacement = params.displacement || 26;

  if (!bars || bars.length < senkouBPeriod) {
    return { tenkan: [], kijun: [], spanA: [], spanB: [], chikou: [], cloudData: [] };
  }

  const times = bars.map((b) => b.time);
  const highs = bars.map((b) => b.high);
  const lows = bars.map((b) => b.low);
  const closes = bars.map((b) => b.close);

  // Estimate time step between bars (in seconds)
  let timeStep = 300;
  if (times.length > 1) {
    timeStep = times[times.length - 1] - times[times.length - 2];
    if (timeStep <= 0) timeStep = 300;
  }

  const getMidpoint = (startIdx, period) => {
    let maxH = -Infinity;
    let minL = Infinity;
    for (let i = startIdx - period + 1; i <= startIdx; i++) {
      if (highs[i] > maxH) maxH = highs[i];
      if (lows[i] < minL) minL = lows[i];
    }
    return (maxH + minL) / 2;
  };

  const tenkan = [];
  const kijun = [];
  const chikou = [];
  const rawSpanA = [];
  const rawSpanB = [];

  for (let i = 0; i < bars.length; i++) {
    // Tenkan-sen
    if (i >= tenkanPeriod - 1) {
      tenkan.push({ time: times[i], value: Math.round(getMidpoint(i, tenkanPeriod) * 1000) / 1000 });
    }

    // Kijun-sen
    if (i >= kijunPeriod - 1) {
      kijun.push({ time: times[i], value: Math.round(getMidpoint(i, kijunPeriod) * 1000) / 1000 });
    }

    // Raw Span A & Span B before displacement
    if (i >= kijunPeriod - 1) {
      const tenkanVal = getMidpoint(i, tenkanPeriod);
      const kijunVal = getMidpoint(i, kijunPeriod);
      rawSpanA.push({ idx: i, time: times[i], value: (tenkanVal + kijunVal) / 2 });
    }

    if (i >= senkouBPeriod - 1) {
      rawSpanB.push({ idx: i, time: times[i], value: getMidpoint(i, senkouBPeriod) });
    }

    // Chikou Span (shifted back displacement bars)
    if (i >= displacement) {
      chikou.push({ time: times[i - displacement], value: closes[i] });
    }
  }

  // Project Span A & Span B forward by displacement (26 bars into future)
  const spanA = [];
  const spanB = [];
  const cloudData = [];

  const lastTime = times[times.length - 1];

  for (let i = 0; i < bars.length; i++) {
    if (i >= senkouBPeriod - 1) {
      const targetIdx = i + displacement;
      let targetTime;
      if (targetIdx < bars.length) {
        targetTime = times[targetIdx];
      } else {
        const futureBarsAhead = targetIdx - (bars.length - 1);
        targetTime = lastTime + futureBarsAhead * timeStep;
      }

      const tenkanVal = getMidpoint(i, tenkanPeriod);
      const kijunVal = getMidpoint(i, kijunPeriod);
      const valA = Math.round(((tenkanVal + kijunVal) / 2) * 1000) / 1000;
      const valB = Math.round(getMidpoint(i, senkouBPeriod) * 1000) / 1000;

      spanA.push({ time: targetTime, value: valA });
      spanB.push({ time: targetTime, value: valB });

      cloudData.push({
        time: targetTime,
        spanA: valA,
        spanB: valB,
        isBullish: valA >= valB,
      });
    }
  }

  return { tenkan, kijun, spanA, spanB, chikou, cloudData };
}

export function calculateMultiEMA(bars, params = {}) {
  const p1 = params.ema1 || 34;
  const p2 = params.ema2 || 89;
  const p3 = params.ema3 || 200;
  const p4 = params.ema4 || 610;

  if (!bars || bars.length === 0) {
    return { ema1: [], ema2: [], ema3: [], ema4: [] };
  }

  const times = bars.map((b) => b.time);
  const closes = bars.map((b) => b.close);

  const calcEMA = (period) => {
    const k = 2 / (period + 1);
    const res = [];
    let prev = closes[0];
    for (let i = 0; i < closes.length; i++) {
      if (i === 0) prev = closes[0];
      else prev = closes[i] * k + prev * (1 - k);
      res.push({ time: times[i], value: Math.round(prev * 1000) / 1000 });
    }
    return res;
  };

  return {
    ema1: calcEMA(p1),
    ema2: calcEMA(p2),
    ema3: calcEMA(p3),
    ema4: calcEMA(p4),
  };
}

export function calculateMACD(bars, params = {}) {
  const fast = params.fast || 12;
  const slow = params.slow || 26;
  const signalPeriod = params.signal || 9;

  if (!bars || bars.length < slow) {
    return { macd: [], signal: [], hist: [] };
  }

  const times = bars.map((b) => b.time);
  const closes = bars.map((b) => b.close);

  const calcEMA = (data, period) => {
    const k = 2 / (period + 1);
    const res = [];
    let prev = data[0];
    for (let i = 0; i < data.length; i++) {
      if (i === 0) prev = data[0];
      else prev = data[i] * k + prev * (1 - k);
      res.push(prev);
    }
    return res;
  };

  const emaFast = calcEMA(closes, fast);
  const emaSlow = calcEMA(closes, slow);

  const macdRaw = [];
  for (let i = 0; i < closes.length; i++) {
    macdRaw.push(emaFast[i] - emaSlow[i]);
  }

  const signalRaw = calcEMA(macdRaw, signalPeriod);

  const macd = [];
  const signal = [];
  const hist = [];

  for (let i = slow - 1; i < closes.length; i++) {
    const mVal = Math.round(macdRaw[i] * 1000) / 1000;
    const sVal = Math.round(signalRaw[i] * 1000) / 1000;
    const hVal = Math.round((mVal - sVal) * 1000) / 1000;

    macd.push({ time: times[i], value: mVal });
    signal.push({ time: times[i], value: sVal });
    hist.push({
      time: times[i],
      value: hVal,
      color: hVal >= 0 ? "rgba(16, 185, 129, 0.7)" : "rgba(239, 68, 68, 0.7)",
    });
  }

  return { macd, signal, hist };
}

export function calculateHMA(bars, params = {}) {
  const period = params.period || 21;

  if (!bars || bars.length === 0) {
    return { hma: [], hmaBull: [], hmaBear: [], latest: null };
  }

  const times = bars.map((b) => b.time);
  const closes = bars.map((b) => b.close);
  const n = closes.length;

  const calcWMA = (data, p) => {
    const res = new Array(data.length);
    const weightSum = (p * (p + 1)) / 2;
    for (let i = 0; i < data.length; i++) {
      if (i < p - 1) {
        let sum = 0;
        let subWeight = 0;
        for (let j = 0; j <= i; j++) {
          const w = j + 1;
          sum += data[j] * w;
          subWeight += w;
        }
        res[i] = subWeight > 0 ? sum / subWeight : data[i];
      } else {
        let sum = 0;
        for (let j = 0; j < p; j++) {
          sum += data[i - p + 1 + j] * (j + 1);
        }
        res[i] = sum / weightSum;
      }
    }
    return res;
  };

  const halfPeriod = Math.max(1, Math.floor(period / 2));
  const sqrtPeriod = Math.max(1, Math.floor(Math.sqrt(period)));

  const wmaHalf = calcWMA(closes, halfPeriod);
  const wmaFull = calcWMA(closes, period);

  const rawHma = new Array(n);
  for (let i = 0; i < n; i++) {
    rawHma[i] = 2 * wmaHalf[i] - wmaFull[i];
  }

  const hmaValues = calcWMA(rawHma, sqrtPeriod);

  const hma = [];
  const hmaBull = [];
  const hmaBear = [];

  for (let i = 0; i < n; i++) {
    const val = Math.round(hmaValues[i] * 1000) / 1000;
    const isBull = i === 0 || hmaValues[i] >= hmaValues[i - 1];
    const item = { time: times[i], value: val, isBull };
    hma.push(item);
    if (isBull) {
      hmaBull.push({ time: times[i], value: val });
    } else {
      hmaBear.push({ time: times[i], value: val });
    }
  }

  return {
    hma,
    hmaBull,
    hmaBear,
    latest: {
      value: hma[hma.length - 1]?.value,
      isBull: hma[hma.length - 1]?.isBull,
    },
  };
}